<template>
    <div class="broadcast-sended-details">
        <div class="broadcast-sended-details-title">{{ $t("wp.sendingStatuses") }}</div>
        <div class="broadcast-sended-details-header">
            <div class="broadcast-sended-details-stats">
                <div class="stat-box success">
                    <div class="stat-label">{{ $t("task.success") }}</div>
                    <div class="stat-value">{{ successCount }}</div>
                </div>
                <div class="stat-box failure">
                    <div class="stat-label">{{ $t("wp.failed") }}</div>
                    <div class="stat-value">{{ failureCount }}</div>
                </div>
                <div class="stat-box enqueued">
                    <div class="stat-label">{{ $t("wp.inProcess") }}</div>
                    <div class="stat-value">{{ enqueuedCount }}</div>
                </div>
            </div>
            <div class="broadcast-sended-details-filters ml-3">
                <a-form-item :label="$t('chatbotv2.state')" class="status-filter">
                    <a-select v-model="statusFilter">
                        <a-select-option value="all">{{ $t("chatWindow.all") }}</a-select-option>
                        <a-select-option value="enqueued">{{ $t("wp.sendingStatus") }}</a-select-option>
                        <a-select-option value="sent">{{ $t("wp.sent") }}</a-select-option>
                        <a-select-option value="delivered">{{ $t("message.delivered") }}</a-select-option>
                        <a-select-option value="read">{{ $t("form.read") }}</a-select-option>
                        <a-select-option value="failed">{{ $t("wp.failed") }}</a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item :label="$t('wp.errorMessage')" class="failure-reason-filter">
                    <a-select v-model="failureReasonFilter">
                        <a-select-option value="all">{{ $t("wp.allErrors") }}</a-select-option>
                        <a-select-option 
                            v-for="reason in distinctFailureReasons" 
                            :value="reason" 
                            :key="reason"
                            :title="reason"
                            class="failure-reason-item"
                        >
                            {{ reason }}
                        </a-select-option>
                    </a-select>
                </a-form-item>
            </div>
        </div>
        <div class="broadcast-sended-details-download">
            <SupsisButton ghost @click="downloadCSV">
                <a-icon type="download" />
                {{ $t("wp.downloadCSV") }}
            </SupsisButton>
            <SupsisButton ghost @click="downloadExcel">
                <a-icon type="file-excel" />
                {{ $t("message.excelDownload") }}
            </SupsisButton>
        </div>
        <div class="broadcast-sended-details-table">
            <SupsisTable :columns="cols" :rows="filteredDetails" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { mapState } from "vuex";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { expertToExcel, exportToCSV } from "../../../../../utils/utils";

export default Vue.extend({
    name: "BroadcastSendedDetails",
    components: { SupsisButton, SupsisTable },
    props: {
        details: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        const status = {
            enqueued: "Gönderimde",
            sent: "Gönderildi",
            delivered: "Teslim Edildi",
            read: "Okundu",
            failed: "Başarısız",
        };
        return {
            cols: [
                {
                    field: "number",
                    key: "number",
                    title: "Telefon Numarası",
                    width: 50,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div>{row.number}</div>;
                    },
                },
                {
                    key: "success",
                    field: "success",
                    title: "Durum",
                    width: 50,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div>{status[row.status] || status.enqueued}</div>;
                    },
                },
                {
                    key: "failureReason",
                    field: "failureReason",
                    title: "Hata Mesajı",
                    width: 50,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <div>{row.failureReason || "N/A"}</div>;
                    },
                },
            ],
            statusFilter: "all",
            failureReasonFilter: "all",
            status,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            user: (state) => state.auth.user,
        }),
        successCount() {
            return this.details.filter((detail) => ["sent", "delivered", "read"].includes(detail.status)).length;
        },
        failureCount() {
            return this.details.filter((detail) => detail.status === "failed").length;
        },
        enqueuedCount() {
            return this.details.filter((detail) => detail.status === "enqueued").length;
        },
        distinctFailureReasons() {
            return [...new Set(this.details.map((detail) => detail.failureReason).filter(Boolean))];
        },
        filteredDetails() {
            return this.details.filter((detail) => {
                const statusMatch = this.statusFilter === "all" || detail.status === this.statusFilter;
                const reasonMatch =
                    this.failureReasonFilter === "all" || detail.failureReason === this.failureReasonFilter;

                return statusMatch && reasonMatch;
            });
        },
    },
    methods: {
        downloadCSV() {
            const csvData = this.filteredDetails.map((detail) => ({
                number: detail.number,
                status: this.status[detail.status] || this.status.enqueued,
                failureReason: detail.failureReason || "N/A",
            }));
            exportToCSV(csvData, "broadcast_details.csv");
        },
        downloadExcel() {
            const excelData = this.filteredDetails.map((detail) => ({
                number: detail.number,
                status: this.status[detail.status] || this.status.enqueued,
                failureReason: detail.failureReason || "N/A",
            }));
            expertToExcel(excelData, "broadcast_details.xlsx", "Broadcast Details");
        },
    },
});
</script>

<style scoped lang="scss">

.ant-select-dropdown-menu-item {
    max-width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.failure-reason-item {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    text-overflow: ellipsis;
    max-width: 300px;
}

.broadcast-sended-details-filters {
    display: flex;
    gap: 10px;
    width: 100%;

    .status-filter {
        flex: 0.3;
    }

    .failure-reason-filter {
        flex: 0.7;
        width: 252px;
    }

    .ant-select {
        width: 100% !important;
    }
}

.broadcast-sended-details {
    display: flex;
    width: 100%;
    flex-direction: column;
    height: 85vh;
    gap: 10px;

    &-title {
        font-size: 18px;
        font-weight: bold;
    }

    &-table {
        height: 70vh;
        overflow-y: auto;
        overflow-x: hidden;
    }

    &-stats {
        display: flex;
        gap: 20px;
        font-size: 16px;

        .stat-box {
            padding: 5px 10px;
            border-radius: 4px;

            &.success {
                background-color: #f6ffed;
                border: 1px solid #b7eb8f;
            }

            &.failure {
                background-color: #fff2f0;
                border: 1px solid #ffccc7;
            }

            &.enqueued {
                background-color: #e6f7ff;
                border: 1px solid #91d5ff;
            }
        }

        .stat-label {
            font-weight: bold;
        }

        .stat-value {
            text-align: center;
            font-size: 24px;
        }
    }

    &-filters {
        display: flex;
        gap: 10px;
        margin-bottom: 10px;
    }

    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
    }

    &-download {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-bottom: 10px;
    }
}
</style>
